import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, signal } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { ItemsFacade, ProjectorItemsEntity, SlimItemsEntity } from '@galabid/gb-dashboard/item/data-access'
import * as ItemsActions from '@galabid/gb-dashboard/item/data-access'
import { GbDashboardItemDataAccessModule } from 'libs/gb-dashboard/item/data-access/src/lib/gb-dashboard-item-data-access.module'
import {
  ItemTableCardType,
  ItemTableWidget,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { Observable, combineLatest, interval, of } from 'rxjs'
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators'
import { GbPresenterWidgetItemTableCardCompactComponent } from '../gb-presenter-widget-item-table-card-compact/gb-presenter-widget-item-table-card-compact.component'
import { GbPresenterWidgetItemTableCardStandardComponent } from '../gb-presenter-widget-item-table-card-standard/gb-presenter-widget-item-table-card-standard.component'
import { PRESENTER_ITEM_TABLE_TEST_ITEMS } from './gb-presenter-widget-item-table.test-items'
import { GbPresenterWidgetPlaceholderComponent } from '../gb-presenter-widget-placeholder/gb-presenter-widget-placeholder.component'
import { TranslateModule } from '@ngx-translate/core'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { SharedUiModule } from '@galabid/shared/ui'

@Component({
  selector: 'gb-presenter-widget-item-table',
  imports: [
    CommonModule,
    GbPresenterWidgetItemTableCardStandardComponent,
    GbPresenterWidgetItemTableCardCompactComponent,
    MatProgressSpinnerModule,
    GbDashboardItemDataAccessModule,
    GbPresenterWidgetPlaceholderComponent,
    TranslateModule,
    SharedUiModule,
  ],
  templateUrl: './gb-presenter-widget-item-table.component.html',
  styleUrl: './gb-presenter-widget-item-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterWidgetItemTableComponent implements OnChanges {
  @Input({ required: true }) itemTableWidget!: ItemTableWidget
  @Input() testData = false
  @Input() isTemplateMode = false
  ItemTableCardType = ItemTableCardType

  testDataSignal = signal<boolean>(false)
  testData$ = toObservable(this.testDataSignal).pipe(shareReplay({ bufferSize: 1, refCount: true }))

  allItems$ = combineLatest([this.itemsFacade.allItems$, this.testData$]).pipe(
    map(([items, useTestData]) => (useTestData ? PRESENTER_ITEM_TABLE_TEST_ITEMS : items)),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  loaded$ = combineLatest([this.itemsFacade.loaded$, this.testData$]).pipe(
    map(([loaded, useTestData]) => (useTestData ? true : loaded)),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  private hasItemTableWidgetChanged(a: ItemTableWidget, b: ItemTableWidget): boolean {
    return (
      a.uuid !== b.uuid ||
      a.rows !== b.rows ||
      a.columns !== b.columns ||
      a.show_item_type !== b.show_item_type ||
      JSON.stringify(a.hidden_items || []) !== JSON.stringify(b.hidden_items || [])
    )
  }

  private previousWidgetUuid: string | null = null

  itemTableWidgetSignal = signal<ItemTableWidget | null>(null)
  itemTableWidget$: Observable<ItemTableWidget> = toObservable(this.itemTableWidgetSignal).pipe(
    filter((widget): widget is ItemTableWidget => !!widget),
    distinctUntilChanged(this.hasItemTableWidgetChanged),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  filteredItems$ = combineLatest([this.allItems$, this.itemTableWidget$]).pipe(
    map(([items, widget]) => {
      const hiddenUuids = new Set((widget.hidden_items || []) as string[])
      return (items || []).filter((item) => !hiddenUuids.has(item.uuid))
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  showItems$ = this.filteredItems$.pipe(map((items) => items.length > 0))

  pageSize$ = this.itemTableWidget$.pipe(
    map((widget) => Math.max(0, (widget.rows || 0) * (widget.columns || 0))),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  slideIntervalSeconds$ = this.itemTableWidget$.pipe(
    map((widget) => Math.max(1, Number(widget.slide_interval || 10))),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  totalPages$ = combineLatest([this.filteredItems$, this.pageSize$]).pipe(
    map(([items, pageSize]) => {
      if (!pageSize || !items?.length) return 1
      return Math.max(1, Math.ceil(items.length / pageSize))
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pageIndex$ = combineLatest([this.filteredItems$, this.pageSize$, this.itemTableWidget$]).pipe(
    switchMap(([items, pageSize, widget]) => {
      if (!pageSize || !items?.length) return of(0)

      if (widget.transition !== 'fade-in') return of(0)

      const totalPages = Math.max(1, Math.ceil(items.length / pageSize))
      const slideIntervalSeconds = Math.max(1, Number(widget.slide_interval || 10))

      return interval(slideIntervalSeconds * 1000).pipe(
        startWith(0),
        map((tick) => tick % totalPages),
      )
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  displayItems$ = combineLatest([this.filteredItems$, this.pageSize$, this.pageIndex$]).pipe(
    map(([items, pageSize, pageIndex]) => {
      if (!pageSize) return []

      const start = pageIndex * pageSize
      return (items || []).slice(start, start + pageSize) as ProjectorItemsEntity[]
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pages$ = combineLatest([this.filteredItems$, this.pageSize$]).pipe(
    map(([items, pageSize]) => {
      if (!pageSize) return []

      const safeItems = (items || []) as ProjectorItemsEntity[]
      const pages: ProjectorItemsEntity[][] = []
      for (let i = 0; i < safeItems.length; i += pageSize) {
        pages.push(safeItems.slice(i, i + pageSize))
      }
      return pages
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  pagesLoop$ = combineLatest([this.pages$, this.totalPages$]).pipe(
    map(([pages, totalPages]) => {
      if (totalPages <= 1) return pages
      return [...pages, ...pages]
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  carouselLoopDurationMs$ = combineLatest([this.slideIntervalSeconds$, this.totalPages$]).pipe(
    map(([intervalSeconds, totalPages]) => {
      if (totalPages <= 1) return 0
      return intervalSeconds * totalPages * 1000
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  carouselLoopTranslatePercent$ = this.totalPages$.pipe(
    map((totalPages) => {
      if (totalPages <= 1) return 0
      return totalPages * 100
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  constructor(
    private itemsFacade: ItemsFacade,
    private colorResolver: GbPresenterColorResolverService,
  ) {}

  loadItems() {
    if (this.isTemplateMode) {
      return
    }
    this.itemsFacade.dispatch(ItemsActions.loadItems({ shouldReset: true, projector: true, limit: 20, offset: 0 }))
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemTableWidget']?.currentValue) {
      const currentWidget = changes['itemTableWidget'].currentValue as ItemTableWidget
      const previousWidget = changes['itemTableWidget'].previousValue as ItemTableWidget | undefined

      const hasWidgetChanged = !previousWidget || this.hasItemTableWidgetChanged(previousWidget, currentWidget)

      if (hasWidgetChanged || !this.itemTableWidgetSignal()) {
        this.itemTableWidgetSignal.set(currentWidget)
      }

      if (hasWidgetChanged || this.previousWidgetUuid !== currentWidget.uuid) {
        this.previousWidgetUuid = currentWidget.uuid
        this.loadItems()
      }
    }

    if (changes['testData']?.currentValue !== undefined) {
      this.testDataSignal.set(!!changes['testData'].currentValue)
    }
  }

  get fontFamily(): string {
    return this.itemTableWidget?.font_family || this.colorResolver.globalFontFamily() || ''
  }

  getItemTypeTranslateKey(item: SlimItemsEntity | ProjectorItemsEntity | undefined): string {
    switch (item?.item_type) {
      case 'bidding':
        return 'ITEMS.TYPE.BIDDING'
      case 'live':
        return 'ITEMS.TYPE.LIVE'
      case 'sealed':
        return 'ITEMS.TYPE.SEALED'
      case 'raffle':
        return 'ITEMS.TYPE.RAFFLE'
      case 'purchase':
        return 'ITEMS.TYPE.PURCHASE'
      case 'donation':
        return 'ITEMS.TYPE.DONATION_SINGULAR'
      default:
        return 'N/A'
    }
  }
}
